import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useContext } from 'react';
import { AlertContext } from '../../../contexts/alert-context';
import type { ReceivingSession } from '../../../data/receiving';
import { receivingSessionsQueryOptions } from '../../../query/query-options';
import type { ReceivingService } from '../../../services/receiving';

/**
 * Shared cancel handler for a receiving session viewed from the session page.
 * Cancels via the backend, optimistically drops the row from the sessions-list
 * cache (the list page's WebSocket invalidation only runs while it's mounted,
 * so without this the list would briefly show the stale row), then navigates
 * back to the list. Used by both the session-header Cancel and the `failed`
 * screen's Cancel.
 */
export function useCancelSession(storeId: string, service: ReceivingService) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addErrorAlert } = useContext(AlertContext);

  return useCallback(
    async (sessionId: string) => {
      try {
        await service.cancel(storeId, sessionId);
        queryClient.setQueryData<ReceivingSession[]>(
          receivingSessionsQueryOptions(storeId).queryKey,
          (sessions) => sessions?.filter((s) => s.session_id !== sessionId)
        );
        void navigate({ to: '/stores/$storeId/receiving', params: { storeId } });
      } catch (err) {
        console.error('receiving: cancel failed', err);
        addErrorAlert("Couldn't cancel the receiving session. Please try again.");
      }
    },
    [storeId, service, queryClient, navigate, addErrorAlert]
  );
}
